import { useMemo, useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  usePricebook,
  usePricebookEntries,
  useProducts,
  useBulkPatchPricebookEntries,
} from '@/hooks/use-api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { formatCurrency } from '@/lib/utils'
import {
  ArrowLeft,
  Plus,
  Trash2,
  Star,
  Pencil,
  SlidersHorizontal,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'
import type { PricebookEntry } from '@/types'
import { BulkAdjustPricesModal, type BulkAdjustTarget } from '@/components/BulkAdjustPricesModal'
import { PricebookChangeHistoryPanel } from '@/components/PricebookChangeHistoryPanel'
import { PriceMatrixPanel } from '@/components/PriceMatrixPanel'
import { explainError } from '@/lib/explain-error'

// Local representation of an entry with optional dirty overlay fields.
// `_dirty_price` / `_dirty_active` hold staged edits until Save/Discard.
// `_deleted` marks a row staged for deletion.
// Rows added via the "Add product" row get `_new = true` and a temp id.
interface EditableEntry extends PricebookEntry {
  _dirty_price?: number
  _dirty_active?: boolean
  _deleted?: boolean
  _new?: boolean
}

type SortKey = 'product_name' | 'product_code' | 'unit_price' | 'currency_iso_code'
type SortDir = 'asc' | 'desc'

export default function PricebookPricesEditor() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data: pricebook, isLoading: pbLoading } = usePricebook(id || '')
  const { data: entriesData, isLoading: entriesLoading } = usePricebookEntries(id || '', {
    limit: 10000,
  })
  const { data: productsData } = useProducts({ limit: 10000, sort_by: 'name', sort_order: 'asc' })
  const patchMutation = useBulkPatchPricebookEntries()

  const serverEntries = useMemo(() => entriesData?.data ?? [], [entriesData])
  const allProducts = productsData?.data ?? []

  // Local editable copy. Refreshed from server whenever server data changes
  // AND we're not currently in edit mode (to avoid clobbering in-flight edits).
  const [editMode, setEditMode] = useState(false)
  // Two ways to look at the same prices.
  //   'currency' — the original one-currency-at-a-time list
  //   'matrix'   — products down, governed currencies across
  // The matrix is the default: it is the only view in which "the Australian
  // price is the same number as the US price" is visible, which is the state
  // 54 products are in.
  const [view, setView] = useState<'matrix' | 'currency'>('matrix')
  const [rows, setRows] = useState<EditableEntry[]>([])

  useEffect(() => {
    if (!editMode) {
      // Junk '000' rows come from a bad SF import; hide them from the editor
      // so they can't be edited, dirtied, or saved. They remain in the DB.
      setRows(serverEntries.filter((e) => e.currency_iso_code !== '000').map((e) => ({ ...e })))
    }
  }, [serverEntries, editMode])

  // Filter / sort controls
  const [search, setSearch] = useState('')
  const [currency, setCurrency] = useState<string>('USD')
  const [sortKey, setSortKey] = useState<SortKey>('product_name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkAdjustOpen, setBulkAdjustOpen] = useState(false)
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false)

  // Currencies present in this pricebook (excludes '000' which was filtered on load).
  const availableCurrencies = useMemo(() => {
    const set = new Set<string>()
    for (const r of rows) {
      if (r.currency_iso_code) set.add(r.currency_iso_code)
    }
    return Array.from(set).sort()
  }, [rows])

  // Default currency for the add-row picker: the currently-filtered currency,
  // or 'USD' if viewing all currencies.
  const addRowDefaultCurrency = currency === 'all' ? 'USD' : currency

  // Add-product inline row state. `addCurrency` follows the filter by default
  // but the user can change it in the footer.
  const [addProductId, setAddProductId] = useState('')
  const [addPrice, setAddPrice] = useState('')
  const [addCurrency, setAddCurrency] = useState<string>('USD')
  useEffect(() => {
    setAddCurrency(addRowDefaultCurrency)
  }, [addRowDefaultCurrency])

  // Products already in this pricebook, keyed by `product_id + currency`. The
  // picker hides products that already have an entry in the currently-selected
  // currency, but still surfaces them for other currencies.
  const existingKeys = useMemo(() => {
    const set = new Set<string>()
    for (const r of rows) {
      if (r._deleted) continue
      set.add(`${r.product_id}__${r.currency_iso_code || ''}`)
    }
    return set
  }, [rows])
  const availableProducts = useMemo(
    () => allProducts.filter((p) => !existingKeys.has(`${p.id}__${addCurrency}`)),
    [allProducts, existingKeys, addCurrency]
  )

  // Dirty detection
  const dirtyRows = rows.filter(
    (r) => r._new || r._deleted || r._dirty_price !== undefined || r._dirty_active !== undefined
  )
  const hasUnsaved = dirtyRows.length > 0

  // Warn on hard-exit (tab close, reload, browser back) when there are
  // unsaved changes.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!hasUnsaved || !editMode) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasUnsaved, editMode])

  // Warn on IN-APP navigation too.
  //
  // beforeunload only covers hard exits, so clicking anything in the sidebar
  // used to discard a screen full of staged price edits with no prompt at all.
  // The usual fix (React Router's useBlocker) needs a data router, and this app
  // mounts BrowserRouter — migrating that is a separate change with its own
  // risk. Intercepting the click in the capture phase gets the same protection
  // without it: router <Link>s render real anchors, so catching the click
  // before React Router sees it lets us cancel the navigation.
  useEffect(() => {
    if (!hasUnsaved || !editMode) return

    const onClickCapture = (e: MouseEvent) => {
      // Let modified clicks (new tab/window) and non-primary buttons through.
      if (e.defaultPrevented || e.button !== 0) return
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

      const anchor = (e.target as HTMLElement | null)?.closest?.('a')
      if (!anchor) return

      const href = anchor.getAttribute('href')
      // Only same-tab, in-app navigation. External links and downloads already
      // trigger beforeunload.
      if (!href || !href.startsWith('/') || anchor.target === '_blank') return
      if (href === window.location.pathname) return

      const count = dirtyRows.length
      const ok = window.confirm(
        `You have ${count} unsaved price change${count === 1 ? '' : 's'}. ` +
          `Leaving this page will discard ${count === 1 ? 'it' : 'them'}.\n\nLeave anyway?`
      )
      if (!ok) {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    document.addEventListener('click', onClickCapture, true)
    return () => document.removeEventListener('click', onClickCapture, true)
  }, [hasUnsaved, editMode, dirtyRows.length])

  // Filter + sort for display (operates on the full `rows` so dirty flags persist)
  const displayRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const textFiltered = q
      ? rows.filter(
          (r) =>
            (r.product_name || '').toLowerCase().includes(q) ||
            (r.product_code || '').toLowerCase().includes(q)
        )
      : rows
    const filtered =
      currency === 'all'
        ? textFiltered
        : textFiltered.filter((r) => r.currency_iso_code === currency)
    const factor = sortDir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      if (sortKey === 'unit_price') {
        const av = a._dirty_price ?? a.unit_price ?? 0
        const bv = b._dirty_price ?? b.unit_price ?? 0
        return (av - bv) * factor
      }
      const av = (a[sortKey] || '').toString().toLowerCase()
      const bv = (b[sortKey] || '').toString().toLowerCase()
      if (av < bv) return -1 * factor
      if (av > bv) return 1 * factor
      return 0
    })
  }, [rows, search, currency, sortKey, sortDir])

  // Clear selection when switching currency so rows filtered-out don't linger.
  useEffect(() => {
    setSelectedIds(new Set())
  }, [currency])

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(k)
      setSortDir('asc')
    }
  }
  const ariaSortFor = (k: SortKey): 'ascending' | 'descending' | 'none' =>
    sortKey === k ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'

  // Row mutations (local only — commit on Save)
  const setPrice = (rowId: string, v: string) => {
    const parsed = parseFloat(v)
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? {
              ...r,
              _dirty_price: v === '' ? undefined : isNaN(parsed) ? r._dirty_price : parsed,
            }
          : r
      )
    )
  }
  const setActive = (rowId: string, checked: boolean) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, _dirty_active: checked } : r)))
  }
  const markDelete = (rowId: string) => {
    setRows((prev) =>
      prev.flatMap((r) => {
        if (r.id !== rowId) return [r]
        if (r._new) return [] // just drop new rows entirely
        return [{ ...r, _deleted: true }]
      })
    )
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.delete(rowId)
      return next
    })
  }
  const undoDelete = (rowId: string) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, _deleted: false } : r)))
  }

  const addRow = () => {
    if (!addProductId || !addPrice.trim()) return
    const parsed = parseFloat(addPrice)
    if (isNaN(parsed) || parsed < 0) {
      toast({
        title: 'Invalid price',
        description: 'Enter a non-negative number.',
        variant: 'destructive',
      })
      return
    }
    const product = allProducts.find((p) => p.id === addProductId)
    if (!product) return
    // Guard against duplicate product+currency (including rows currently hidden
    // by the currency filter). Message points the user to the right currency.
    if (existingKeys.has(`${product.id}__${addCurrency}`)) {
      toast({
        title: 'Already in pricebook',
        description: `${product.name} already has a ${addCurrency} entry. Switch the currency filter to edit it.`,
        variant: 'destructive',
      })
      return
    }
    const tempId = `_new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    setRows((prev) => [
      ...prev,
      {
        id: tempId,
        pricebook_id: id || '',
        product_id: product.id,
        product_name: product.name,
        product_code: product.product_code,
        unit_price: parsed,
        currency_iso_code: addCurrency,
        is_active: true,
        use_standard_price: false,
        _new: true,
      } as EditableEntry,
    ])
    setAddProductId('')
    setAddPrice('')
  }

  const applyBulkAdjustment = (next: Array<{ id: string; unit_price: number }>) => {
    const byId = new Map(next.map((n) => [n.id, n.unit_price]))
    setRows((prev) =>
      prev.map((r) => {
        const v = byId.get(r.id)
        if (v === undefined) return r
        return { ...r, _dirty_price: v }
      })
    )
  }

  const bulkTargets: BulkAdjustTarget[] = useMemo(() => {
    return rows
      .filter((r) => selectedIds.has(r.id) && !r._deleted)
      .map((r) => ({
        id: r.id,
        product_name: r.product_name,
        current_price: r._dirty_price ?? r.unit_price,
      }))
  }, [rows, selectedIds])

  const handleDiscard = () => {
    // Mirror the initial-load filter — '000' rows never enter the editor.
    setRows(serverEntries.filter((e) => e.currency_iso_code !== '000').map((e) => ({ ...e })))
    setSelectedIds(new Set())
    setAddProductId('')
    setAddPrice('')
    setDiscardConfirmOpen(false)
  }

  const handleSave = async () => {
    if (!id) return
    const creates = rows
      .filter((r) => r._new && !r._deleted)
      .map((r) => ({
        product_id: r.product_id,
        unit_price: r._dirty_price ?? r.unit_price,
        currency_iso_code: r.currency_iso_code || 'USD',
        is_active: r._dirty_active ?? r.is_active,
      }))
    const updates = rows
      .filter(
        (r) =>
          !r._new && !r._deleted && (r._dirty_price !== undefined || r._dirty_active !== undefined)
      )
      .map((r) => ({
        id: r.id,
        ...(r._dirty_price !== undefined ? { unit_price: r._dirty_price } : {}),
        ...(r._dirty_active !== undefined ? { is_active: r._dirty_active } : {}),
      }))
    const deletes = rows.filter((r) => !r._new && r._deleted).map((r) => r.id)

    if (creates.length === 0 && updates.length === 0 && deletes.length === 0) {
      toast({ title: 'Nothing to save', description: 'No changes detected.' })
      return
    }

    try {
      const res = await patchMutation.mutateAsync({
        pricebookId: id,
        payload: { creates, updates, deletes },
      })
      toast({
        title: 'Changes saved',
        description: `${res.created} added, ${res.updated} updated, ${res.deleted} removed.`,
      })
      setSelectedIds(new Set())
      setEditMode(false)
    } catch (err) {
      const axErr = err as { response?: { data?: { error?: string; message?: string } } }
      toast({
        title: 'Save failed',
        description:
          axErr?.response?.data?.error ||
          axErr?.response?.data?.message ||
          explainError(err, 'Unknown error'),
        variant: 'destructive',
      })
    }
  }

  if (pbLoading || entriesLoading) {
    return <div className="p-6 text-muted-foreground">Loading pricebook…</div>
  }

  const sortIcon = (k: SortKey) =>
    sortKey === k ? (
      sortDir === 'asc' ? (
        <ArrowUp className="inline h-3 w-3 ml-1" />
      ) : (
        <ArrowDown className="inline h-3 w-3 ml-1" />
      )
    ) : null

  return (
    <div className="space-y-4" role="main" aria-label="Manage pricebook prices">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/pricebooks')}
            aria-label="Back to pricebooks"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <CategoryBadge type="pricebook" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              {pricebook?.name || 'Pricebook'}
              {pricebook?.is_standard && (
                <Star
                  className="h-4 w-4 text-warning fill-warning"
                  aria-label="Standard pricebook"
                />
              )}
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage the product prices in this pricebook.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* View switch. The matrix leads because it is the view that makes a
              missing or copied currency price obvious. */}
          <div className="inline-flex rounded-md border" role="group" aria-label="Price list view">
            <Button
              variant={view === 'matrix' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-r-none"
              onClick={() => setView('matrix')}
              aria-pressed={view === 'matrix'}
            >
              All currencies
            </Button>
            <Button
              variant={view === 'currency' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-l-none"
              onClick={() => setView('currency')}
              aria-pressed={view === 'currency'}
              disabled={editMode && hasUnsaved}
              title={
                editMode && hasUnsaved
                  ? 'Save or discard your changes before switching view'
                  : undefined
              }
            >
              One currency
            </Button>
          </div>
          {view === 'currency' &&
            (!editMode ? (
              <Button onClick={() => setEditMode(true)} aria-label="Switch to edit mode">
                <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
                Edit prices
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => (hasUnsaved ? setDiscardConfirmOpen(true) : setEditMode(false))}
                >
                  Discard
                </Button>
                <Button onClick={handleSave} disabled={!hasUnsaved || patchMutation.isPending}>
                  {patchMutation.isPending
                    ? 'Saving…'
                    : `Save ${dirtyRows.length} ${dirtyRows.length === 1 ? 'change' : 'changes'}`}
                </Button>
              </>
            ))}
        </div>
      </div>

      {/* Toolbar: search + currency filter + bulk adjust */}
      {view === 'matrix' ? (
        id && <PriceMatrixPanel pricebookId={id} />
      ) : (
        <>
          <Card>
            <CardContent className="p-3 flex items-center gap-3 flex-wrap">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by product name or code…"
                className="max-w-sm"
                aria-label="Filter entries"
              />
              <div className="flex items-center gap-2">
                <label
                  htmlFor="pricebook-currency-filter"
                  className="text-sm text-muted-foreground"
                >
                  Currency
                </label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger
                    id="pricebook-currency-filter"
                    className="h-9 w-[180px]"
                    aria-label="Filter entries by currency"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All currencies</SelectItem>
                    {availableCurrencies.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {editMode && selectedIds.size > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setBulkAdjustOpen(true)}
                    aria-label="Bulk adjust selected prices"
                  >
                    <SlidersHorizontal className="h-4 w-4 mr-2" aria-hidden="true" />
                    Bulk adjust ({selectedIds.size})
                  </Button>
                )}
                <span className="text-xs text-muted-foreground">
                  {displayRows.filter((r) => !r._deleted).length} /{' '}
                  {rows.filter((r) => !r._deleted).length} entries
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Entries table */}
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" aria-label="Pricebook price entries">
                <thead className="bg-muted/50">
                  <tr className="border-b">
                    {editMode && (
                      <th className="w-10 p-2">
                        <Checkbox
                          checked={(() => {
                            const visible = displayRows.filter((r) => !r._deleted)
                            return visible.length > 0 && visible.every((r) => selectedIds.has(r.id))
                          })()}
                          onCheckedChange={(v) => {
                            if (v) {
                              setSelectedIds(
                                new Set(displayRows.filter((r) => !r._deleted).map((r) => r.id))
                              )
                            } else {
                              setSelectedIds(new Set())
                            }
                          }}
                          aria-label="Select all visible entries"
                        />
                      </th>
                    )}
                    <th
                      className="text-left p-2 font-medium"
                      aria-sort={ariaSortFor('product_name')}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort('product_name')}
                        className="inline-flex items-center font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded-sm"
                      >
                        Product {sortIcon('product_name')}
                      </button>
                    </th>
                    <th
                      className="text-left p-2 font-medium"
                      aria-sort={ariaSortFor('product_code')}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort('product_code')}
                        className="inline-flex items-center font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded-sm"
                      >
                        Code {sortIcon('product_code')}
                      </button>
                    </th>
                    <th
                      className="text-right p-2 font-medium"
                      aria-sort={ariaSortFor('unit_price')}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort('unit_price')}
                        className="inline-flex items-center font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded-sm ml-auto"
                      >
                        Price {sortIcon('unit_price')}
                      </button>
                    </th>
                    <th
                      className="text-center p-2 font-medium"
                      aria-sort={ariaSortFor('currency_iso_code')}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort('currency_iso_code')}
                        className="inline-flex items-center font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded-sm mx-auto"
                      >
                        Currency {sortIcon('currency_iso_code')}
                      </button>
                    </th>
                    <th className="text-center p-2 font-medium">Active</th>
                    {editMode && <th className="w-12 p-2" aria-label="Row actions" />}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((r) => {
                    const currentPrice = r._dirty_price ?? r.unit_price
                    const currentActive = r._dirty_active ?? r.is_active
                    const isDirty =
                      r._new ||
                      r._deleted ||
                      r._dirty_price !== undefined ||
                      r._dirty_active !== undefined
                    return (
                      <tr
                        key={r.id}
                        className={`border-t ${r._deleted ? 'line-through opacity-50' : ''} ${isDirty && !r._deleted ? 'bg-[#FFC857]/20' : ''}`}
                      >
                        {editMode && (
                          <td className="p-2">
                            <Checkbox
                              checked={selectedIds.has(r.id)}
                              disabled={r._deleted}
                              onCheckedChange={(v) => {
                                setSelectedIds((prev) => {
                                  const next = new Set(prev)
                                  if (v) next.add(r.id)
                                  else next.delete(r.id)
                                  return next
                                })
                              }}
                              aria-label={`Select ${r.product_name || r.id}`}
                            />
                          </td>
                        )}
                        <td className="p-2">
                          {r.product_name || '—'}
                          {r._new && (
                            <Badge variant="outline" className="ml-2 text-[10px]">
                              New
                            </Badge>
                          )}
                        </td>
                        <td className="p-2 text-muted-foreground">{r.product_code || '—'}</td>
                        <td className="p-2 text-right">
                          {editMode && !r._deleted ? (
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={currentPrice}
                              onChange={(e) => setPrice(r.id, e.target.value)}
                              className="h-8 w-28 text-right tabular-nums ml-auto"
                              aria-label={`Price for ${r.product_name || r.id}`}
                            />
                          ) : (
                            <span className="tabular-nums">
                              {formatCurrency(currentPrice, r.currency_iso_code || 'USD')}
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          <span className="tabular-nums">{r.currency_iso_code || '—'}</span>
                        </td>
                        <td className="p-2 text-center">
                          {editMode && !r._deleted ? (
                            <Switch
                              checked={currentActive}
                              onCheckedChange={(v) => setActive(r.id, v)}
                              aria-label={`Toggle active for ${r.product_name || r.id}`}
                            />
                          ) : (
                            <Badge
                              variant={currentActive ? 'success' : 'destructive'}
                              className="text-xs"
                            >
                              {currentActive ? 'Active' : 'Inactive'}
                            </Badge>
                          )}
                        </td>
                        {editMode && (
                          <td className="p-2 text-right">
                            {r._deleted ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => undoDelete(r.id)}
                                aria-label="Undo remove"
                              >
                                Undo
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => markDelete(r.id)}
                                aria-label={`Remove ${r.product_name || r.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                  {displayRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={editMode ? 7 : 5}
                        className="p-6 text-center text-muted-foreground"
                      >
                        {search || currency !== 'all'
                          ? 'No entries match your filter.'
                          : 'No pricing entries yet.'}
                      </td>
                    </tr>
                  )}
                </tbody>

                {/* Add product row — edit mode only */}
                {editMode && (
                  <tfoot className="border-t bg-muted/20">
                    <tr>
                      {/* Spacer for select column */}
                      <td className="p-2" />
                      <td colSpan={2} className="p-2">
                        <RecordCombobox
                          records={availableProducts}
                          value={addProductId}
                          onValueChange={setAddProductId}
                          displayField={(p: { name?: string; product_code?: string }) =>
                            p.name && p.product_code
                              ? `${p.name} (${p.product_code})`
                              : p.name || '—'
                          }
                          placeholder="Add a product…"
                          searchPlaceholder="Search products…"
                          emptyMessage="No products available to add."
                          aria-label="Add product"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={addPrice}
                          onChange={(e) => setAddPrice(e.target.value)}
                          placeholder="0.00"
                          className="h-8 w-28 text-right tabular-nums ml-auto"
                          aria-label="Price for new product"
                        />
                      </td>
                      <td className="p-2 text-center">
                        <Select value={addCurrency} onValueChange={setAddCurrency}>
                          <SelectTrigger
                            className="h-8 w-[90px] mx-auto"
                            aria-label="Currency for new product"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {/*
                          Offer any currency already in this pricebook *plus* the
                          standard set so users can introduce a new currency to
                          the pricebook (e.g., add the first EUR entry) without
                          needing one to exist first.
                        */}
                            {Array.from(
                              new Set<string>([
                                ...availableCurrencies,
                                'USD',
                                'GBP',
                                'EUR',
                                'CAD',
                                'AUD',
                                'DKK',
                              ])
                            )
                              .sort()
                              .map((c) => (
                                <SelectItem key={c} value={c}>
                                  {c}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2 text-center text-xs text-muted-foreground">Active</td>
                      <td className="p-2 text-right">
                        <Button
                          size="sm"
                          onClick={addRow}
                          disabled={!addProductId || !addPrice.trim()}
                          aria-label="Add product to pricebook"
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </CardContent>
          </Card>

          {/* History panel */}
        </>
      )}

      {id && <PricebookChangeHistoryPanel pricebookId={id} disabled={editMode && hasUnsaved} />}

      {/* Bulk adjust modal */}
      <BulkAdjustPricesModal
        open={bulkAdjustOpen}
        onOpenChange={setBulkAdjustOpen}
        targets={bulkTargets}
        onApply={applyBulkAdjustment}
      />

      {/* Discard confirmation */}
      <AlertDialog open={discardConfirmOpen} onOpenChange={setDiscardConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have {dirtyRows.length} unsaved {dirtyRows.length === 1 ? 'change' : 'changes'}.
              Discarding will revert the table to its last saved state.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscard}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
